"use client";

// libraries/components/src/components/pnr-locator/form/index.tsx

import {
  useForm,
  useController,
  type UseFormReturn,
  type FieldPath,
  type RegisterOptions,
} from "react-hook-form";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type JSX,
} from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Button } from "@jetblue/kit/button";
import { InputText as ClientInput } from "@jetblue/kit/input-text";
import he from "he";

import {
  useHealthMutation,
  type HealthResponse,
} from "@jb-manage-trips/api/mutations/health";

import {
  cleanDiacritics,
  cleanForeignLetters,
} from "../../../../helpers/clean";
import { sanitizeConfirmationCode } from "../../../../helpers/confirmation";
import { createManageFlightsService } from "../services/manage-flights.client";
import { useManageTripsConfig } from "@jb-manage-trips/runtime-config";
import {
  getDurationMs,
  getErrorTelemetry,
  getTelemetryStart,
  logManageTripsEvent,
  type ManageTripsTelemetryLocatorType,
  type ManageTripsTelemetryOperation,
  type ManageTripsTelemetryResultRoute,
} from "@jb-manage-trips/fe-logger/manage-trips-events";
import { useManageTripsAuthFlow } from "../features/use-manage-trips-auth-flow";
import type { Labels } from "./types";
import type {
  PnrFormValues as FormValues,
  PnrLocatorResponse as PnrResponse,
  HealthCheckResponse,
} from "@jb-manage-trips/types/pnr";

import {
  defaultValues,
  FieldError,
  formatDobInput,
  getStatusFromUnknown,
  isTicketNumber,
  isValidDobString,
  sixCharAlphanumeric,
  thirteenDigitNumeric,
} from "./helpers";
function getLocatorType(
  confirmationCode: string | undefined,
): ManageTripsTelemetryLocatorType {
  const normalized = sanitizeConfirmationCode(confirmationCode ?? "");
  if (!normalized) {
    return "unknown";
  }
  return isTicketNumber(normalized) ? "ticket_number" : "record_locator";
}

function withCurrentLanguagePrefix(route: string, pathname: string): string {
  const normalizedRoute = route.startsWith("/") ? route : `/${route}`;
  const languageMatch = /^\/([a-z]{2})(?=\/)/i.exec(pathname);

  if (!languageMatch?.[1]) {
    return normalizedRoute;
  }

  const languagePrefix = `/${languageMatch[1].toLowerCase()}`;

  if (normalizedRoute.toLowerCase().startsWith(`${languagePrefix}/`)) {
    return normalizedRoute;
  }

  return `${languagePrefix}${normalizedRoute}`.replaceAll(/\/{2,}/g, "/");
}

export default function PnrLocatorFormClient(
  properties: Readonly<{ labels: Readonly<Labels> }>,
): JSX.Element {
  return (
    <Suspense>
      <PnrLocatorFormClientInner {...properties} />
    </Suspense>
  );
}

function PnrLocatorFormClientInner(
  properties: Readonly<{ labels: Readonly<Labels> }>,
): JSX.Element {
  const {
    errorMessage: authError,
    isChecking,
    isLoading: authLoading,
  } = useManageTripsAuthFlow();

  const { labels } = properties;

  const form: UseFormReturn<FormValues> = useForm<FormValues>({
    defaultValues,
    mode: "onTouched",
  });

  const {
    control,
    clearErrors,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = form;

  const useField = <TName extends FieldPath<FormValues>>(
    name: TName,
    rules?: RegisterOptions<FormValues, TName>,
  ) =>
    useController<FormValues, TName>({
      control,
      name,
      ...(rules ? { rules } : {}),
    });

  const lastNameMsgs = {
    required: labels.lastName?.requiredMessage ?? "Please enter a last name",
  };

  const codeMsgs = {
    required:
      labels.confirmationCode?.requiredMessage ??
      "Please enter a confirmation code or ticket #",
    invalid:
      labels.confirmationCode?.invalidLocatorMessage ??
      "Enter either a 6-character code (letters/numbers) or a 13-digit ticket number",
  };

  const dobMsgs = {
    required:
      labels.dateOfBirth?.requiredMessage ?? "Date of birth is required",
    invalid:
      labels.dateOfBirth?.invalidMessage ??
      "Enter a valid date of birth (MM/DD/YYYY)",
  };

  const { field: lastNameField, fieldState: lastNameState } = useField(
    "lastName",
    {
      validate: (v?: string) => {
        const value = (v ?? "").trim();
        if (!value) {
          return lastNameMsgs.required;
        }
        return true;
      },
    },
  );

  const { field: codeField, fieldState: codeState } = useField(
    "confirmationCode",
    {
      validate: (v?: string) => {
        const value = (v ?? "").trim();
        if (!value) {
          return codeMsgs.required;
        }

        const normalized = sanitizeConfirmationCode(value);

        return (
          sixCharAlphanumeric.test(normalized) ||
          thirteenDigitNumeric.test(normalized) ||
          codeMsgs.invalid
        );
      },
    },
  );

  const normalizedCode = useMemo(
    () => sanitizeConfirmationCode(codeField.value),
    [codeField.value],
  );

  const requiresDob = useMemo(
    () => isTicketNumber(normalizedCode),
    [normalizedCode],
  );

  const { field: dobField, fieldState: dobState } = useField("dateofBirth", {
    validate: (v?: string) => {
      if (!requiresDob) {
        return true;
      }

      const value = (v ?? "").trim();

      if (!value) {
        return dobMsgs.required;
      }

      if (!isValidDobString(value)) {
        return dobMsgs.invalid;
      }

      return true;
    },
  });

  useEffect(() => {
    if (!requiresDob) {
      setValue("dateofBirth", "", {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      clearErrors("dateofBirth");
    }
  }, [clearErrors, requiresDob, setValue]);

  const router = useRouter();
  const search = useSearchParams();
  const pathname = usePathname();

  const [serverError, setServerError] = useState<string | null>(null);
  const [isPendingTransition, startTransition] = useTransition();
  const abortReference = useRef<AbortController | null>(null);

  const manageTripsConfig = useManageTripsConfig();
  const { MANAGE_TRIPS: manageTrips, DOTCOM_FLAGS: dotcomFlags } =
    manageTripsConfig;

  const { ROUTES: routes } = manageTrips;
  // eslint-disable-next-line  @typescript-eslint/no-unnecessary-condition -- Prefer this pattern
  const flags = manageTrips.FLAGS ?? {};
  const softLaunchLegacyHandoffEnabled =
    flags.SOFT_LAUNCH_LEGACY_HANDOFF_ENABLED === true;

  const navigateToManageTripsRoute = useCallback(
    (route: string) => {
      if (softLaunchLegacyHandoffEnabled) {
        globalThis.location.assign(route);
        return;
      }

      startTransition(() => {
        router.push(route);
      });
    },
    [router, softLaunchLegacyHandoffEnabled, startTransition],
  );

  const service = useMemo(
    () => createManageFlightsService(manageTripsConfig),
    [manageTripsConfig],
  );

  const platformOS = useMemo<"iOS" | "Android" | "Other">(() => {
    if (typeof navigator === "undefined") {
      return "Other";
    }
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) {
      return "iOS";
    }
    if (ua.includes("Android")) {
      return "Android";
    }
    return "Other";
  }, []);

  const newAbortSignal = useCallback((): AbortSignal => {
    if (abortReference.current) {
      abortReference.current.abort();
    }
    const controller = new AbortController();
    abortReference.current = controller;
    return controller.signal;
  }, []);

  const preprocess = useCallback(
    (values: FormValues): FormValues => {
      let lastName = values.lastName.trim();

      if (dotcomFlags.MYB_REPLACE_LAST_NAME_ACCENTS) {
        lastName = cleanDiacritics(lastName);
        lastName = cleanForeignLetters(lastName);
      }

      if (lastName.includes("'") || lastName.includes("-")) {
        lastName = lastName.replaceAll(/['-]/g, " ");
      }
      if (lastName.includes(".")) {
        lastName = lastName.replaceAll(".", "");
      }

      const confirmationCode = values.confirmationCode
        ? sanitizeConfirmationCode(values.confirmationCode)
        : "";

      const dateofBirth = isTicketNumber(confirmationCode)
        ? (values.dateofBirth ?? "").trim()
        : "";

      return {
        confirmationCode,
        dateofBirth,
        lastName,
      };
    },
    [dotcomFlags.MYB_REPLACE_LAST_NAME_ACCENTS],
  );

  const healthMutation = useHealthMutation();

  const submitFlow = useCallback(
    async (raw: FormValues, signal: AbortSignal) => {
      const telemetryStart = getTelemetryStart();
      const values = preprocess(raw);
      const locatorType = getLocatorType(values.confirmationCode);
      let failedOperation: ManageTripsTelemetryOperation =
        "anonymous_locator_submit";
      let resultRoute: ManageTripsTelemetryResultRoute | undefined;
      let pnrState: string | undefined;
      let emailExists: boolean | undefined;
      let phoneExists: boolean | undefined;

      logManageTripsEvent({
        eventName: "anonymous_locator_flow_started",
        feature: "pnr-locator",
        flow: "anonymous",
        locatorType,
        operation: "anonymous_locator_submit",
        status: "started",
      });
      try {
        failedOperation = "pnr_lookup";
        const response: {
          healthCheckResponse?: HealthCheckResponse;
          pnrServiceResponse: PnrResponse;
        } = await service.getFullPnrCheckNonAuthenticated(values, signal, {
          includeHealth: false,
        });
        const pnrServiceResponse = response.pnrServiceResponse;
        const b6PNRRecordLocator =
          pnrServiceResponse.b6PNRLocatorRS?.b6PNRRecordLocator;

        // Throw a typed Error when the locator is an empty string so the existing
        // catch/telemetry and service.displayErrorMessage logic handles it.
        if (b6PNRRecordLocator === "" || b6PNRRecordLocator == null) {
          throw Object.assign(new Error("PNR record locator not found"), {
            status: 404,
          });
        }

        failedOperation = "pnr_health_check";
        const effectiveHealth: HealthResponse =
          await healthMutation.mutateAsync({
            lastName: values.lastName,
            b6PNRRecordLocator,
          });
        const {
          emailExist = false,
          phoneNumberExist = false,
          pnrState: effectivePnrState,
        } = effectiveHealth;
        emailExists = emailExist;
        phoneExists = phoneNumberExist;
        pnrState = effectivePnrState;
        const shouldRedirectQualifierForm =
          service.getRedirectStatusToQualifier(emailExist, phoneNumberExist);
        resultRoute = "itinerary";
        if (shouldRedirectQualifierForm) {
          resultRoute = "add-trip-email";
        } else {
          const localHealthCheckUrl = Boolean(
            process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL,
          );
          if (pnrState === "normal" && !localHealthCheckUrl) {
            resultRoute = "sabre-dx";
          }
        }

        logManageTripsEvent({
          durationMs: getDurationMs(telemetryStart),
          emailExists,
          eventName: "anonymous_locator_flow_completed",
          feature: "pnr-locator",
          flow: "anonymous",
          locatorType,
          operation: "route_decision",
          phoneExists,
          pnrState,
          resultRoute,
          status: "success",
        });
        if (shouldRedirectQualifierForm) {
          const q = new URLSearchParams({
            emailExist: String(emailExist),
            lastName: values.lastName,
            locale: search.get("locale") ?? "",
            phoneNumberExist: String(phoneNumberExist),
            pnr: b6PNRRecordLocator,
          });

          const route = `${withCurrentLanguagePrefix(
            routes.QUALIFIER_FORM,
            pathname,
          )}?${q.toString()}`;

          navigateToManageTripsRoute(route);
          return;
        }
        if (resultRoute === "sabre-dx") {
          const queryParameters = Object.fromEntries(search.entries());
          const url = service.buildSabreDxUrl(
            values.lastName,
            b6PNRRecordLocator,
            queryParameters,
            platformOS,
          );
          globalThis.location.assign(url);
          return;
        }
        const itinerarySearch = new URLSearchParams({
          lastName: values.lastName,
          locale: search.get("locale") ?? "",
          pnr: b6PNRRecordLocator,
        });

        const route = `${withCurrentLanguagePrefix(
          routes.ITINERARY,
          pathname,
        )}?${itinerarySearch.toString()}`;

        navigateToManageTripsRoute(route);
      } catch (error: unknown) {
        logManageTripsEvent({
          ...getErrorTelemetry(error),
          durationMs: getDurationMs(telemetryStart),
          emailExists,
          eventName: "anonymous_locator_flow_failed",
          feature: "pnr-locator",
          flow: "anonymous",
          locatorType,
          operation: failedOperation,
          phoneExists,
          pnrState,
          resultRoute,
          status: "failure",
        });
        throw error;
      }
    },
    [
      healthMutation,
      navigateToManageTripsRoute,
      pathname,
      platformOS,
      preprocess,
      routes.ITINERARY,
      routes.QUALIFIER_FORM,
      search,
      service,
    ],
  );

  const onSubmit = handleSubmit(async (raw) => {
    setServerError(null);
    const signal = newAbortSignal();

    try {
      await submitFlow(raw, signal);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      const fallbackSubmitErrors = {
        confirmationSubmitError:
          "We couldn’t process your confirmation code. Please try again.",
        serviceError: "We’re having trouble right now. Please try again.",
        ticketNumberSubmitError:
          "We couldn’t process your ticket number. Please try again.",
        tripsNotFoundError: "We couldn’t find any matching trips.",
      };

      const submitErrors = Array.isArray(labels.submitErrors)
        ? (labels.submitErrors[0] ?? fallbackSubmitErrors)
        : (labels.submitErrors ?? fallbackSubmitErrors);

      const status = getStatusFromUnknown(error);
      const message = service.displayErrorMessage(
        status,
        raw.confirmationCode,
        submitErrors,
      );

      setServerError(message);
    }
  });

  const onSubmitVoid: NonNullable<JSX.IntrinsicElements["form"]["onSubmit"]> =
    useCallback(
      (event) => {
        void onSubmit(event);
      },
      [onSubmit],
    );

  const isHealthPending = healthMutation.isPending;
  const isBusy =
    isSubmitting ||
    isHealthPending ||
    isPendingTransition ||
    authLoading ||
    isChecking;

  const submitCTA = labels.submitCTA ?? {
    initial: "Continue",
    pressed: "Continue",
  };

  return (
    <form className="max-w-md space-y-4" noValidate onSubmit={onSubmitVoid}>
      {authError && (
        <div className="text-s-body font-semibold text-red" role="alert">
          {authError}
        </div>
      )}

      {serverError && (
        <div className="text-s-body font-semibold text-red" role="alert">
          {he.decode(serverError)}
        </div>
      )}

      {healthMutation.isError && (
        <div className="text-s-body font-semibold text-red" role="alert">
          {healthMutation.error instanceof Error
            ? healthMutation.error.message
            : "We couldn’t verify your itinerary. Please try again."}
        </div>
      )}

      <div className="flex flex-col">
        <p className="mt-1 text-[10px] font-semibold tracking-widest text-slate-gray uppercase">
          {labels.lastName?.description ?? ""}
        </p>
        <ClientInput
          label={labels.lastName?.label ?? "Last name"}
          name={lastNameField.name}
          onBlur={lastNameField.onBlur}
          onChange={lastNameField.onChange}
          value={lastNameField.value}
        />
        <FieldError message={lastNameState.error?.message} />
      </div>

      <div className="flex flex-col">
        <p className="mt-1 text-[10px] font-semibold tracking-widest text-slate-gray uppercase">
          {labels.confirmationCode?.description ?? ""}
        </p>
        <ClientInput
          label={
            labels.confirmationCode?.label ?? "Confirmation code or ticket #"
          }
          name={codeField.name}
          onBlur={codeField.onBlur}
          onChange={codeField.onChange}
          value={normalizedCode}
        />
        <FieldError message={codeState.error?.message} />
      </div>

      {requiresDob && (
        <div className="flex flex-col">
          <p className="mt-1 text-[10px] font-semibold tracking-widest text-slate-gray uppercase">
            {labels.dateOfBirth?.description ?? ""}
          </p>
          <ClientInput
            inputMode="numeric"
            label={labels.dateOfBirth?.label ?? "Date of Birth (MM/DD/YYYY)"}
            name={dobField.name}
            onBlur={dobField.onBlur}
            onChange={(event) => {
              const target = event.currentTarget;
              dobField.onChange(formatDobInput(target.value));
            }}
            value={dobField.value ?? ""}
          />
          <FieldError message={dobState.error?.message} />
        </div>
      )}

      <div className="pt-2">
        <Button
          className="w-full md:w-auto"
          disabled={isBusy}
          size="large"
          type="submit"
        >
          {isBusy ? submitCTA.pressed : submitCTA.initial}
        </Button>
      </div>
    </form>
  );
}
